import {
    getCombinedDuplicateMicroserviceIndexes,
    getJfrogTargetRepositories,
    checkIfAllReposAssigned,
    extractNewGithubRepositoryNames,
    extractGithubNames,
    isNoneTechStack,
} from '.';
import { COMMON, JFROG_DEPENDENCY_OPTION, MIGRATE_EXISTING_OPTION, ONBOARDING_CATEGORY, PIPELINE_SETUP_TYPE, PIPELINE_TYPE_GLOBAL_TEMPLATE } from './constant';

export const isMultiRegion = (deploymentPattern) => /multi[\s-]?region/i.test(deploymentPattern || '');
export const isMultiModule = (deploymentPattern) => /multi[\s-]?module/i.test(deploymentPattern || '');
export const isMultiRegionOrModule = (deploymentPattern) => isMultiRegion(deploymentPattern) || isMultiModule(deploymentPattern);

export const isProjectSectionFilled = ({ order = {} }) => {
    const etaInMonths = Number(order?.sandboxPocDetails?.etaForRegistrationInMonths);
    const isEtaForRegistrationValid = Number.isFinite(etaInMonths) && etaInMonths > 0 && etaInMonths <= 3;

    // Onboarding category is always required
    if (!order?.onboardingType) {
        return false;
    }

    // For NEW onboarding type
    if (order.onboardingType === ONBOARDING_CATEGORY.NEW) {
        // Check if isUsingAzureBoards is selected
        if (!order?.isUsingAzureBoards) {
            return false;
        }

        // If using Azure Boards = Yes, ADO Project Name is required
        if (order.isUsingAzureBoards === COMMON.YES && !order?.adoProjectName) {
            return false;
        }

        // If a project is selected, its organization must also be available
        if (order.isUsingAzureBoards === COMMON.YES && order?.adoProjectName && !order?.adoOrganization) {
            return false;
        }

        // Environment is required for NEW onboarding
        if (!order?.environment) {
            return false;
        }

        // Sandbox/Personal environment requires registeredInSnow answer
        if (order.environment === COMMON.SANDBOX_PERSONAL) {
            if (!order?.registeredInSnow) {
                return false;
            }
            // If not registered, all 3 sandbox POC details are required
            if (order.registeredInSnow === COMMON.NO) {
                if (!order?.sandboxPocDetails?.reasonForNotRegistering || !order?.sandboxPocDetails?.purposeOfUsingSandbox || !isEtaForRegistrationValid) {
                    return false;
                }
            }
        }

        return true;
    }

    // For MIGRATE onboarding type
    if (order.onboardingType === ONBOARDING_CATEGORY.MIGRATE) {
        // Check if migration source is selected (ADO or GitHub)
        if (!order?.isMigratingADO) {
            return false;
        }

        // If migrating from ADO, ADO Project Name is required
        if (order.isMigratingADO === MIGRATE_EXISTING_OPTION.ADO && !order?.adoProjectName) {
            return false;
        }

        // If a project is selected, its organization must also be available
        if (order.isMigratingADO === MIGRATE_EXISTING_OPTION.ADO && order?.adoProjectName && !order?.adoOrganization) {
            return false;
        }

        // If migrating from GitHub, GitHub Organization Unit is required
        if (order.isMigratingADO === MIGRATE_EXISTING_OPTION.GITHUB && !order?.githubOrgUnit) {
            return false;
        }

        // Environment is required for MIGRATE onboarding
        if (!order?.environment) {
            return false;
        }

        return true;
    }

    return false;
};

export const isValidAppName = (appName) => {
    if (!appName || appName === '' || appName === undefined) {
        return true;
    }
    return /^[a-zA-Z][a-zA-Z0-9_\-\s]{2,62}[a-zA-Z0-9]$/.test(appName);
};

export const isValidNoScenarioBusinessApplication = (value) => {
    if (!value || value === '' || value === undefined) {
        return true;
    }
    const trimmed = value.trim();
    if (trimmed === '') {
        return false;
    }
    return /^(?=.*[a-zA-Z0-9])[a-zA-Z0-9 _()/&.,-]{4,100}$/.test(trimmed);
};

export const isValidNoScenarioApplicationService = (value) => {
    if (!value || value === '' || value === undefined) {
        return true;
    }
    const trimmed = value.trim();
    if (trimmed === '') {
        return false;
    }
    return /^(?=.*[a-zA-Z0-9])[a-zA-Z0-9 _()-]{4,64}$/.test(trimmed);
};

export const isValidNoScenarioApplicationSupportGroup = (value) => {
    if (!value || value === '' || value === undefined) {
        return true;
    }
    const trimmed = value.trim();
    if (trimmed === '') {
        return false;
    }
    return /^(?=.*[a-zA-Z0-9])[a-zA-Z0-9 _()-]{4,64}$/.test(trimmed);
};

export const isValidAppShortName = (appShortName) => {
    if (!appShortName || appShortName === '' || appShortName === undefined) {
        return true;
    }
    return /^[a-zA-Z0-9]{2,8}$/.test(appShortName);
};

export const isValidPortfolio = (portfolio) => {
    if (!portfolio || portfolio === '' || portfolio === undefined) {
        return true;
    }
    return portfolio.length <= 40;
};

export const isValidName = (val) => {
    if (!val || val === '' || val === undefined) {
        return true;
    }
    const trimmed = val.trim();
    return /^[a-z0-9]+(-[a-z0-9]+)*$/.test(trimmed) && trimmed.length >= 1 && !/\s/.test(val) && !trimmed.startsWith('-') && !trimmed.endsWith('-');
};

export const isValidRepoName = (val) => {
    if (!val || val === '' || val === undefined) {
        return true;
    }
    const trimmed = val.trim();
    return (
        /^[a-z0-9]+([-.][a-z0-9]+)*$/.test(trimmed) &&
        trimmed.length >= 3 &&
        trimmed.length <= 99 &&
        !/\s/.test(val) &&
        !trimmed.startsWith('-') &&
        !trimmed.endsWith('-') &&
        !trimmed.startsWith('.') &&
        !trimmed.endsWith('.')
    );
};

export const isValidSonarProjectName = (val) => {
    if (!val || val === '' || val === undefined) {
        return true;
    }
    const trimmed = val.trim();
    return (
        /^[a-z0-9]+([-_][a-z0-9]+)*$/.test(trimmed) &&
        trimmed.length >= 2 &&
        trimmed.length <= 100 &&
        !/\s/.test(val) &&
        !trimmed.startsWith('-') &&
        !trimmed.endsWith('-') &&
        !trimmed.startsWith('_') &&
        !trimmed.endsWith('_')
    );
};

export const isValidMicroserviceName = (val) => {
    if (!val || val === '' || val === undefined) {
        return true;
    }
    const trimmed = val.trim();
    return (
        /^[a-z0-9]+([-.][a-z0-9]+)*$/.test(trimmed) &&
        trimmed.length >= 3 &&
        trimmed.length <= 62 &&
        !/\s/.test(val) &&
        !trimmed.startsWith('-') &&
        !trimmed.endsWith('-') &&
        !trimmed.startsWith('.') &&
        !trimmed.endsWith('.')
    );
};

export const isValidServiceAccount = (serviceAccount) => {
    if (!serviceAccount || serviceAccount === '' || serviceAccount === undefined) {
        return true;
    }
    if (/\s/.test(serviceAccount)) {
        return false;
    }
    return /^[a-zA-Z0-9_-]{4,64}$/.test(serviceAccount);
};

export const isValidArtifactoryName = (artifactoryName) => {
    if (!artifactoryName || artifactoryName === '' || artifactoryName === undefined) {
        return true;
    }
    if (/\s/.test(artifactoryName)) {
        return false;
    }
    return /^[a-zA-Z][a-zA-Z0-9_-]{2,63}$/.test(artifactoryName);
};

export const isUserAccessConfigFilled = ({ teams = [] }) => {
    if (!Array.isArray(teams) || teams.length === 0) return false;

    const hasDuplicateNames = new Set(teams.map((t) => t.name.trim().toLowerCase())).size !== teams.length;
    if (hasDuplicateNames) return false;

    return teams.every((team) => {
        if (!team.name) return false;

        // If team exists in org, adding members is optional
        if (team.teamExistsInOrg === true) {
            return true;
        }

        // If team doesn't exist in org, adding members is mandatory
        const hasMembersAdded = Array.isArray(team.members) && team.members.length > 0;
        return hasMembersAdded;
    });
};

export const isAssignReposConfigFilled = ({ teams = [] }) => {
    return Array.isArray(teams) && teams.length > 0 && teams.every((eachTeam) => Array.isArray(eachTeam?.repos) && eachTeam.repos.length > 0);
};

export const isMigrateRepoConfigFilled = ({
    reposToMigrateList = [],
    order = {},
    pipelineClassificationData = [],
    variableGroupExtractionData = [],
    argoCDConfigData = [],
}) => {
    const hasRepos = Array.isArray(reposToMigrateList) && reposToMigrateList.length > 0;
    const hasPipelineSelection = order?.isMigratingADOPipelines === COMMON.YES || order?.isMigratingADOPipelines === COMMON.NO;
    const hasArgoCDSelection = order?.isMigratingArgoCD === COMMON.YES || order?.isMigratingArgoCD === COMMON.NO;

    if (!hasRepos || !hasPipelineSelection || !hasArgoCDSelection) return false;

    if (order?.isMigratingArgoCD === COMMON.YES) {
        const argoCDEntries = argoCDConfigData || [];
        if (argoCDEntries.length === 0) return false;
        const allArgoCDFilled = argoCDEntries.every((item) => !!item?.adoArgoCDRepoName && !!item?.newGithubArgoCDRepoName);
        if (!allArgoCDFilled) return false;
    }

    if (order?.isMigratingADOPipelines === COMMON.YES) {
        const classificationEntries = pipelineClassificationData || [];
        if (classificationEntries.length === 0) return false;

        const globalTemplateRepos = classificationEntries.filter((item) => item?.pipelineType === PIPELINE_TYPE_GLOBAL_TEMPLATE);
        // Exclude "None" tech stack repos — they only need pipelineType + techStack, no further fields required
        const buildRepos = classificationEntries.filter(
            (item) => item?.pipelineType !== PIPELINE_TYPE_GLOBAL_TEMPLATE && !isNoneTechStack(item?.selectedTechStack)
        );
        const noneRepos = classificationEntries.filter(
            (item) => item?.pipelineType !== PIPELINE_TYPE_GLOBAL_TEMPLATE && isNoneTechStack(item?.selectedTechStack)
        );

        // Variable integration applies to every non-global pipeline, including None-tech-stack repos.
        const allNonGlobalRepos = classificationEntries.filter((item) => item?.pipelineType !== PIPELINE_TYPE_GLOBAL_TEMPLATE);

        // "None" tech stack repos are valid as long as selectedTechStack is set
        const allNoneReposFilled = noneRepos.every((item) => !!item?.selectedTechStack);
        if (!allNoneReposFilled) return false;

        const allNonGlobalVariableGroupsFilled = allNonGlobalRepos.every((item) => {
            const variableGroupEntry = (Array.isArray(variableGroupExtractionData) ? variableGroupExtractionData : []).find(
                (entry) => entry?.repoName === item?.repoName
            );
            const apiReturnedNoVariableGroups =
                variableGroupEntry && (!Array.isArray(variableGroupEntry.variableGroupNames) || variableGroupEntry.variableGroupNames.length === 0);

            if (apiReturnedNoVariableGroups) return true;
            if (item?.migrateVariables === COMMON.NO) return true;
            if (!Array.isArray(item?.variableGroupNames)) return false;
            if (item.variableGroupNames.length === 0 && !item?.migrateVariables) return true;
            if (!item?.migrateVariables) return false;
            return item.variableGroupNames.length > 0;
        });
        if (!allNonGlobalVariableGroupsFilled) return false;

        // CLASSIC / YAML — globalTemplate must be auto-populated from templates API
        const allBuildFilled = buildRepos.every((item) => !!item?.globalTemplate);
        if (!allBuildFilled) return false;

        const allBuildTechStackFilled = buildRepos.every((item) => !!item?.selectedTechStack);
        if (!allBuildTechStackFilled) return false;

        // GLOBAL_TEMPLATE — deploymentPattern AND globalTemplate must be selected, plus variable integration fields
        const allGlobalTemplateFilled = globalTemplateRepos.every((item) => {
            if (!item?.selectedTechStack) return false;
            if (!item?.buildPattern) return false;
            if (!item?.deploymentPattern || !item?.globalTemplate) return false;

            // if variable integration is enabled, variableGroupNames must be selected (unless API returned no variable groups)
            const variableGroupEntry = (Array.isArray(variableGroupExtractionData) ? variableGroupExtractionData : []).find(
                (entry) => entry?.repoName === item?.repoName
            );
            const apiReturnedNoVariableGroups =
                variableGroupEntry && (!Array.isArray(variableGroupEntry.variableGroupNames) || variableGroupEntry.variableGroupNames.length === 0);

            if (!apiReturnedNoVariableGroups) {
                if (!Array.isArray(item?.variableGroupNames) || item.variableGroupNames.length === 0) return false;
            }

            const pattern = item.deploymentPattern;
            if (isMultiRegion(pattern) && (!Array.isArray(item.multiRegion) || item.multiRegion.length === 0)) return false;
            if (isMultiModule(pattern) && (!Array.isArray(item.multiModule) || item.multiModule.length === 0)) return false;
            return true;
        });
        if (!allGlobalTemplateFilled) return false;
    }

    return true;
};

export const isGithubADGroupConfigFilled = ({ validationState = {}, order = {} }) => {
    const hasValidationErrors =
        validationState?.isDuplicateGithubADGroupMember ||
        validationState?.isAdGroupMemberNotEligible ||
        validationState?.isMemberMissingEmail ||
        validationState?.isInvalidDomain ||
        validationState?.isDuplicateAdGroupServiceAccount ||
        !!validationState?.adGroupServiceAccountValidationError;

    if (hasValidationErrors) return false;
    const requiresServiceAccount = order?.githubADGroupCORPPVTHasServiceAccount === COMMON.YES;
    const hasServiceAccounts = Array.isArray(order?.githubADGroupCORPPVTServiceAccounts) && order.githubADGroupCORPPVTServiceAccounts.length > 0;

    if (requiresServiceAccount && !hasServiceAccounts) return false;

    return true;
};

export const isRepoConfigValid = (config) => {
    if (!config?.repositoryName) return false;
    if (config.resolveError) return false;
    if (config.isNoneBuildTool) {
        return true;
    }
    if (config.jfrogGlobalRepo) {
        return !!config.jfrogRepoName;
    }
    return config.isValidated === true;
};

// Determines if a repo's JFrog config has already been settled (via API, error, or no-build-tool flag).
// "Resolved" means: we know what to do with this repo and no API call is needed.
// isNoneBuildTool alone is sufficient — it means the repo has no build tool and needs no API resolution.
// isValidated tracks user confirmation separately and is NOT required here (see isRepoConfigValid for that).
export const isRepoResolved = (cfg) => cfg && (cfg.jfrogGlobalRepo || cfg.isValidated || cfg.resolveError || cfg.isNoneBuildTool || cfg.resolveAttempted);

export const isSonarQubeConfigFilled = ({ config = {}, order = {}, validForm = {}, validationStatus = {} }) => {
    const hasSonarGroupServiceAccounts = Array.isArray(config?.serviceAccountsFromApi) && config.serviceAccountsFromApi.length > 0;

    if (hasSonarGroupServiceAccounts) {
        if (!config?.sonarGroupServiceAccount) {
            return false;
        }

        if (config?.sonarGroupServiceAccountValid !== true || validForm?.isServiceAccountValid !== true) {
            return false;
        }

        if (!config?.isServiceAccountExists) return false;

        if (config?.isServiceAccountExists === COMMON.YES) {
            return !!config?.sonarGroupServiceAccount;
        }

        if (config?.isServiceAccountExists === COMMON.NO) {
            const hasValidAppFields = order.applicationName && order.appShortName;
            if (!hasValidAppFields) return false;
            if (validationStatus?.isAppShortNameExist !== false) return false;
            return true;
        }

        return false;
    }

    if (!config.hasServiceAccount) return false;

    if (config.hasServiceAccount === COMMON.YES) {
        const hasValidServiceAccount = config.serviceAccount && validForm?.isServiceAccountValid === true;
        if (!hasValidServiceAccount) return false;
    }

    if (config.hasServiceAccount === COMMON.NO) {
        const hasValidAppFields = order.applicationName && order.appShortName;
        if (!hasValidAppFields) return false;

        if (validationStatus?.isAppShortNameExist !== false) return false;
    }

    return true;
};

export const isJfrogConfigFilled = ({ config = {}, order = {}, reposToMigrateList = [] }) => {
    // Get target repositories
    const repositories = getJfrogTargetRepositories({ order, reposToMigrateList });

    // No repositories selected yet, so the integration question isn't applicable
    if (repositories.length === 0) {
        return true;
    }

    // Once repositories are available, the Yes/No selection is mandatory
    if (!config?.requiresIntegration) {
        return false;
    }

    // If user doesn't require JFrog integration, validation passes
    if (config?.requiresIntegration === COMMON.NO) {
        return true;
    }

    // If user requires JFrog integration (Yes)
    if (config?.requiresIntegration === COMMON.YES) {
        const dependencyRepoOption = config?.dependencyRepoOption;

        // Dependency management option must be selected (public-only vs public+local)
        if (!dependencyRepoOption) {
            return false;
        }

        // Option 2 (public+local) requires the repo-name question to be answered YES to proceed
        if (dependencyRepoOption === JFROG_DEPENDENCY_OPTION.PUBLIC_AND_LOCAL) {
            if (!config?.hasArtifactoryName || config.hasArtifactoryName === COMMON.NO) {
                return false;
            }
            // At least one repo type (RESOLVE / UPLOAD) must be selected
            if (!Array.isArray(config?.jfrogRepoTypes) || config.jfrogRepoTypes.length === 0) {
                return false;
            }
        }

        // Unknown option value should fail validation
        const isKnownDependencyOption =
            dependencyRepoOption === JFROG_DEPENDENCY_OPTION.PUBLIC_ONLY || dependencyRepoOption === JFROG_DEPENDENCY_OPTION.PUBLIC_AND_LOCAL;
        if (!isKnownDependencyOption) {
            return false;
        }

        // Check each repository has a valid config
        return repositories.every((repoName) => {
            const repoConfig = config?.repoConfigurations?.find((c) => c.repositoryName === repoName);
            return !!repoConfig && isRepoConfigValid(repoConfig);
        });
    }

    return false;
};

export const isSonarQubeIntegrationConfigFilled = ({ config = {}, order = {}, validForm = {}, isAppShortNameExist, reposToMigrateList = [] }) => {
    // No repositories selected yet, so the integration question isn't applicable
    const repositories = getJfrogTargetRepositories({ order, reposToMigrateList });
    if (repositories.length === 0) {
        return true;
    }

    // Once repositories are available, the Yes/No selection is mandatory
    if (!config?.requiresIntegration) {
        return false;
    }

    // If user doesn't require SonarQube integration, validation passes
    if (config?.requiresIntegration === COMMON.NO) {
        return true;
    }

    const isMigrationFlow = order?.onboardingType === ONBOARDING_CATEGORY.MIGRATE;
    const hasSonarGroupServiceAccounts = Array.isArray(config?.serviceAccountsFromApi) && config.serviceAccountsFromApi.length > 0;

    // Helper: validate the service-account section regardless of scenario
    const isServiceAccountSectionValid = () => {
        if (hasSonarGroupServiceAccounts) {
            if (!config?.sonarGroupServiceAccount) {
                return false;
            }

            if (config?.sonarGroupServiceAccountValid !== true || validForm?.isServiceAccountValid !== true) {
                return false;
            }

            if (!config?.isServiceAccountExists) return false;

            if (config?.isServiceAccountExists === COMMON.YES) {
                return !!config?.sonarGroupServiceAccount;
            }

            if (config?.isServiceAccountExists === COMMON.NO) {
                return !!(order?.applicationName && order?.appShortName) && isAppShortNameExist === false;
            }

            return false;
        }

        if (!config?.hasServiceAccount) return false;

        if (config.hasServiceAccount === COMMON.YES) {
            return !!(config?.serviceAccount && validForm?.isServiceAccountValid === true);
        }

        if (config.hasServiceAccount === COMMON.NO) {
            return !!(order?.applicationName && order?.appShortName) && isAppShortNameExist === false;
        }

        return false;
    };

    // If user requires SonarQube integration (Yes)
    if (config?.requiresIntegration === COMMON.YES) {
        // Validation must be performed
        if (!config?.validationData) {
            return false;
        }

        // Check if there are any non-onboarded repos
        const scenarioResults = config?.validationData?.results || [];
        const hasScenarioNonOnboardedRepos = scenarioResults.some((repo) => repo?.availableInSonar === false);
        const hasScenarioOnboardedRepos = scenarioResults.some((repo) => repo?.availableInSonar === true);

        // Scenario 1: All repos onboarded
        if (hasScenarioOnboardedRepos && !hasScenarioNonOnboardedRepos) {
            // For MIGRATE flow, user must select whether to modify project names
            if (isMigrationFlow) {
                if (!config?.shouldModifyProjectName) {
                    return false;
                }
            }
            return isServiceAccountSectionValid();
        }

        // Scenario 2: None onboarded - service account flow required
        if (!hasScenarioOnboardedRepos && hasScenarioNonOnboardedRepos) {
            return isServiceAccountSectionValid();
        }

        // Scenario 3: Partial onboarding
        if (hasScenarioOnboardedRepos && hasScenarioNonOnboardedRepos) {
            // For MIGRATE flow, user must select whether to modify project names for onboarded repos
            if (isMigrationFlow) {
                if (!config?.shouldModifyProjectName) {
                    return false;
                }
            }
            return isServiceAccountSectionValid();
        }
    }

    return false;
};

export const validateTeams = (teams) => {
    return (
        Array.isArray(teams) &&
        teams.length > 0 &&
        teams.every((team) => {
            if (!team.name) return false;
            if (!Array.isArray(team.repos) || team.repos.length === 0) return false;

            // If team exists in org, adding members is optional
            if (team.teamExistsInOrg === true) {
                return true;
            }

            // If team doesn't exist in org, adding members is mandatory
            const hasMembersAdded = Array.isArray(team.members) && team.members.length > 0;
            return hasMembersAdded;
        })
    );
};

export const isMicroserviceConfigValid = (rowData) => {
    if (!rowData) return false;

    const { applicationType, applicationMicroserviceName, newgithubRepositoryName, templateLanguage, templateBuildTool, templateFrameWork, templateName } =
        rowData;

    const isNonEmptyString = (val) => typeof val === 'string' && val.trim().length > 0;

    const mandatoryFieldsValid =
        isNonEmptyString(applicationType) &&
        isValidMicroserviceName(applicationMicroserviceName) &&
        isValidRepoName(newgithubRepositoryName) &&
        isNonEmptyString(applicationMicroserviceName) &&
        isNonEmptyString(newgithubRepositoryName) &&
        isNonEmptyString(templateLanguage);

    // If None is selected, mandatory fields + codeRepositoryStack are required
    if (isNonEmptyString(templateLanguage) && templateLanguage === COMMON.NONE) {
        return mandatoryFieldsValid && isNonEmptyString(rowData?.codeRepositoryStack);
    }

    // If a language (not None) is selected, template fields are required
    if (isNonEmptyString(templateLanguage) && templateLanguage !== COMMON.NONE) {
        return mandatoryFieldsValid && isNonEmptyString(templateBuildTool) && isNonEmptyString(templateFrameWork) && isNonEmptyString(templateName);
    }

    return mandatoryFieldsValid;
};

export const validateMicroserviceConfigs = (configs) => {
    const duplicates = getCombinedDuplicateMicroserviceIndexes(configs);
    if (duplicates?.length > 0) {
        return {
            isValid: false,
            message: 'Repo configuration is invalid due to duplicates.',
            data: { duplicates },
        };
    }

    let validationMessage = {
        isValid: true,
        message: 'Repo configuration is valid.',
        data: { duplicates: [] },
    };

    configs?.forEach((config, index) => {
        if (!validationMessage.isValid) return;

        const isEmpty = (val) => !val || val.trim?.()?.length === 0;

        if (config?.isClone === true) {
            validationMessage = {
                isValid: false,
                message: `Cloned configuration at index ${index} must be modified.`,
                data: { index },
            };
            return;
        }

        if (isEmpty(config?.applicationType)) {
            validationMessage = {
                isValid: false,
                message: `Application Type is required at index ${index}.`,
                data: { index },
            };
            return;
        }

        if (isEmpty(config?.applicationMicroserviceName)) {
            validationMessage = {
                isValid: false,
                message: `Microservice Name is required and cannot be empty at index ${index}.`,
                data: { index },
            };
            return;
        }

        if (!isValidMicroserviceName(config?.applicationMicroserviceName)) {
            validationMessage = {
                isValid: false,
                message: `Microservice Name at index ${index} is invalid. Must be 3-62 characters, lowercase only, may include hyphens or dots (never at start or end).`,
                data: { index },
            };
            return;
        }

        if (isEmpty(config?.newgithubRepositoryName)) {
            validationMessage = {
                isValid: false,
                message: `GitHub Repository Name is required and cannot be empty at index ${index}.`,
                data: { index },
            };
            return;
        }

        if (!isValidRepoName(config?.newgithubRepositoryName)) {
            validationMessage = {
                isValid: false,
                message: `GitHub Repository Name at index ${index} is invalid. Must be 3-99 characters, lowercase only, may include hyphens or dots (never at start or end).`,
                data: { index },
            };
            return;
        }

        if (isEmpty(config?.templateLanguage)) {
            validationMessage = {
                isValid: false,
                message: `Template Language is required at index ${index}. Please select a language or 'None'.`,
                data: { index },
            };
            return;
        }

        if (!isEmpty(config?.templateLanguage) && config?.templateLanguage === COMMON.NONE) {
            if (isEmpty(config?.codeRepositoryStack)) {
                validationMessage = {
                    isValid: false,
                    message: `Code Repository Stack is required at index ${index} when Language is None.`,
                    data: { index },
                };
                return;
            }
        }

        if (!isEmpty(config?.templateLanguage) && config?.templateLanguage !== COMMON.NONE) {
            if (isEmpty(config?.templateBuildTool)) {
                validationMessage = {
                    isValid: false,
                    message: `Build Tool is required at index ${index} when Template Language is selected.`,
                    data: { index },
                };
                return;
            }

            if (isEmpty(config?.templateFrameWork)) {
                validationMessage = {
                    isValid: false,
                    message: `Framework is required at index ${index} when Template Language is selected.`,
                    data: { index },
                };
                return;
            }

            if (isEmpty(config?.templateName)) {
                validationMessage = {
                    isValid: false,
                    message: `Template Name is required at index ${index} when Template Language is selected.`,
                    data: { index },
                };
            }
        }
    });

    return validationMessage;
};

export const isMicroserviceConfigFilled = ({ order = {} }) => {
    return (
        Array.isArray(order?.microservicesConfigs) &&
        order?.microservicesConfigs.length > 0 &&
        validateMicroserviceConfigs(order?.microservicesConfigs)?.isValid &&
        order.microservicesConfigs.every((config) => isMicroserviceConfigValid(config))
    );
};

export const isNewOnboardingPipelineConfigFilled = ({ pipelineConfigs, order, isPipelineEligible }) => {
    const hasAnyValidRepo = Array.isArray(order?.microservicesConfigs) && order.microservicesConfigs.some((c) => isMicroserviceConfigValid(c));

    // Pipeline section is not shown — skip validation
    if (!hasAnyValidRepo) return true;

    // User chose not to onboard pipelines
    if (order?.isOnboardingPipelines === COMMON.NO) return true;

    // No API templates matched any repo's tech stack — pipeline section is hidden
    if (isPipelineEligible === false) return true;

    // User must answer the pipeline onboarding radio
    if (!order?.isOnboardingPipelines) return false;

    if (isPipelineEligible === null || isPipelineEligible === undefined) {
        const hasSaved = Array.isArray(pipelineConfigs) && pipelineConfigs.some((c) => c.pipelineSetupType);
        if (!hasSaved) return false;
    }

    // Yes selected — configs must be populated
    if (!Array.isArray(pipelineConfigs) || pipelineConfigs.length === 0) return false;

    const microserviceConfigs = order?.microservicesConfigs || [];

    return microserviceConfigs.every((microConfig, idx) => {
        if (!isMicroserviceConfigValid(microConfig)) return true;
        if (microConfig?.templateLanguage === COMMON.NONE) return true;

        const config = pipelineConfigs[idx];
        if (!config) return false;

        if (config.isTemplateAvailable === false) return true;

        if (!config.pipelineSetupType) {
            // Template confirmed available — user must select a setup type
            if (config.isTemplateAvailable === true) return false;
            // isTemplateAvailable not yet determined (saved data, modal not opened) — skip
            return true;
        }

        if (config.pipelineSetupType === PIPELINE_SETUP_TYPE.BUILD) {
            return !!(config.buildPattern && config.globalTemplate && config.cataloguePath);
        }

        if (config.pipelineSetupType === PIPELINE_SETUP_TYPE.BUILD_AND_DEPLOYMENT) {
            const hasDeployFields = !!(config.buildPattern && config.deploymentMechanism && config.deploymentPattern && config.cataloguePath);
            if (config.isDirectMechanismStack) return hasDeployFields;
            if (config.isDirectMechanismStack === undefined && !config.branchingStrategy && hasDeployFields) return true;
            return !!(config.branchingStrategy && hasDeployFields);
        }

        return false;
    });
};

export const getConditionalValidations = (order, validationStatus) => {
    const conditional = [];

    const isNewSandboxFlow = order?.onboardingType === ONBOARDING_CATEGORY.NEW && order?.environment === COMMON.SANDBOX_PERSONAL;

    const isNoScenarioTextMode =
        order?.onboardingType === ONBOARDING_CATEGORY.NEW && order?.environment === COMMON.SANDBOX_PERSONAL && order?.registeredInSnow === COMMON.NO;

    if (order?.onboardingType === ONBOARDING_CATEGORY.NEW) {
        if (order?.isUsingAzureBoards === COMMON.YES) {
            conditional.push({ name: 'adoProjectName', value: order?.adoProjectName });
            conditional.push({ name: 'adoOrganization', value: order?.adoOrganization });
        }
        conditional.push({
            name: 'isUsingAzureBoards',
            value: order?.isUsingAzureBoards !== COMMON.YES ? true : order?.isUsingAzureBoards,
        });
        conditional.push({
            name: 'isEachDatabaseConfigValid',
            value: Array.isArray(order?.microservicesConfigs) && order.microservicesConfigs.every((config) => isMicroserviceConfigValid(config)),
        });
        conditional.push({ name: 'isDatabaseConfigsValidWithNoDuplicates', value: validateMicroserviceConfigs(order?.microservicesConfigs)?.isValid });
        conditional.push({
            name: 'isNewOnboardingPipelineConfigFilled',
            value: isNewOnboardingPipelineConfigFilled({
                pipelineConfigs: validationStatus.pipelineConfigs,
                order,
                isPipelineEligible: validationStatus.isPipelineEligible,
            }),
        });

        if (isNewSandboxFlow) {
            conditional.push({
                name: 'registeredInSnow',
                value: order?.registeredInSnow === COMMON.YES || order?.registeredInSnow === COMMON.NO,
            });
        }

        // Project section - Registering in Snow as "No" requires additional details
        if (isNoScenarioTextMode) {
            conditional.push({
                name: 'reasonForNotRegistering',
                value: !!order?.sandboxPocDetails?.reasonForNotRegistering,
            });
            conditional.push({
                name: 'purposeOfUsingSandbox',
                value: !!order?.sandboxPocDetails?.purposeOfUsingSandbox,
            });
            conditional.push({
                name: 'etaForRegistrationInMonths',
                value: Number(order?.sandboxPocDetails?.etaForRegistrationInMonths) > 0,
            });
        }

        // Application section - Registering in Snow as "No" requires valid business application, application service, application support group
        if (isNoScenarioTextMode) {
            conditional.push({
                name: 'isValidNoScenarioBusinessApplication',
                value: isValidNoScenarioBusinessApplication(order?.businessApplication),
            });
            conditional.push({
                name: 'isValidNoScenarioApplicationService',
                value: isValidNoScenarioApplicationService(order?.applicationService),
            });
            conditional.push({
                name: 'isValidNoScenarioApplicationSupportGroup',
                value: isValidNoScenarioApplicationSupportGroup(order?.applicationSupportGroup),
            });
        }
    }

    if (order?.onboardingType === ONBOARDING_CATEGORY.MIGRATE) {
        if (order?.isMigratingADO === MIGRATE_EXISTING_OPTION.ADO) {
            conditional.push({ name: 'adoProjectName', value: order?.adoProjectName });
            conditional.push({ name: 'adoOrganization', value: order?.adoOrganization });
        }
        if (order?.isMigratingADO === MIGRATE_EXISTING_OPTION.GITHUB) {
            conditional.push({
                name: 'githubOrganizationUnit',
                value: order?.githubOrgUnit,
            });
        }
        conditional.push({
            name: 'isMigrateRepoConfigFilled',
            value: isMigrateRepoConfigFilled({
                reposToMigrateList: validationStatus.reposToMigrateList,
                order,
                pipelineClassificationData: validationStatus.pipelineClassificationData,
                variableGroupExtractionData: validationStatus.variableGroupExtractionData,
                argoCDConfigData: validationStatus.argoCDConfigData,
            }),
        });
        conditional.push({ name: 'isMigratingADO', value: order?.isMigratingADO });
    }

    return conditional;
};

export const isReadyToSubmitCost = ({ order, validationStatus = {} }) => {
    const conditions = [
        { name: 'sector', value: order?.sector },
        { name: 'teams', value: isUserAccessConfigFilled({ teams: validationStatus.teams }) },
    ];
    const failedConditions = conditions.filter((condition) => !condition.value);
    const isValid = failedConditions.length === 0;
    return {
        isValid,
        message: isValid ? 'All conditions passed' : `Failed conditions: ${failedConditions.map((c) => c.name).join(', ')}`,
    };
};

export const isReadyToSubmit = ({ order, options = {}, validationStatus = {} }) => {
    const { ignoreEstimatedCostDetails = false } = options;
    const isADO = order?.isMigratingADO === MIGRATE_EXISTING_OPTION.ADO;
    const availableRepos =
        order?.onboardingType === ONBOARDING_CATEGORY.NEW
            ? (extractNewGithubRepositoryNames(order) || []).filter(Boolean)
            : (extractGithubNames(isADO, validationStatus.reposToMigrateList || []) || []).filter(Boolean);

    const conditions = [
        { name: 'appOwner', value: order?.appOwner },
        { name: 'applicationService', value: order?.applicationService },
        { name: 'businessApplication', value: order?.businessApplication },
        { name: 'capability', value: order?.capability },
        { name: 'onboardingType', value: order?.onboardingType },
        { name: 'cmpHCode', value: order?.cmpHCode },
        { name: 'isValidBugetCode', value: validationStatus.isValidBudgetCode },
        { name: 'isValidPortfolio', value: isValidPortfolio(order?.portfolio) },
        { name: 'applicationSupportGroup', value: order?.applicationSupportGroup },
        { name: 'sector', value: order?.sector },
        { name: 'environment', value: order?.environment },
        { name: 'isUserAccessConfigFilled', value: isUserAccessConfigFilled({ teams: validationStatus.teams }) },
        // { name: 'isGithubADGroupConfigFilled', value: isGithubADGroupConfigFilled({ order }) },
        {
            name: 'isAssignReposConfigFilled',
            value: isAssignReposConfigFilled({ teams: validationStatus.teams }) && checkIfAllReposAssigned(validationStatus.teams || [], availableRepos),
        },
        {
            name: 'isSonarQubeIntegrationConfigFilled',
            value: isSonarQubeIntegrationConfigFilled({
                config: validationStatus.sonarQubeConfig,
                order,
                validForm: validationStatus.validForm,
                isAppShortNameExist: validationStatus.isAppShortNameExist,
                reposToMigrateList: validationStatus.reposToMigrateList,
            }),
        },
        {
            name: 'isJfrogConfigFilled',
            value: isJfrogConfigFilled({
                config: validationStatus.jfrogConfig,
                order,
                reposToMigrateList: validationStatus.reposToMigrateList,
            }),
        },
    ];

    if (!ignoreEstimatedCostDetails) {
        conditions.push({ name: 'hasEstimatedCostDetails', value: order?.estimatedCost?.details?.length > 0 });
    }

    const conditionalConditions = getConditionalValidations(order, validationStatus);
    const allConditions = [...conditions, ...conditionalConditions];

    const failedConditions = allConditions.filter((condition) => !condition.value);
    const isValid = failedConditions.length === 0;

    return {
        isValid,
        message: isValid ? 'All conditions passed' : `Failed conditions: ${failedConditions.map((c) => c.name).join(', ')}`,
    };
};
